import { createLogger } from '@anddone/ajs-logger';
import type { ApiResponse, TransactionSettings } from '@anddone/ajs-util';

const logger = createLogger('SDK', 'OrchestratorApiClient');

export class OrchestratorApiClient {
  /**
   * Make an API call to the given endpoint.
   *
   * @param endpoint - Fully-qualified URL to call
   * @param options - Standard fetch RequestInit options
   * @returns Promise resolving to the parsed response body
   */
  public async apiCall<TResponse = unknown>(
    endpoint: string,
    options?: RequestInit,
  ): Promise<TResponse> {
    logger.debug('api-call', `Calling ${endpoint}`);

    const response = await fetch(endpoint, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...options?.headers,
      },
    });

    if (!response.ok) {
      logger.error('api-call', `Request failed: ${response.status} ${response.statusText}`, {
        endpoint,
        status: response.status,
      });
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    return response.json() as Promise<TResponse>;
  }

  /**
   * Fetch merchant and payment intent settings from the settings endpoint.
   *
   * @param hubUrl - Hub base URL for the current environment
   * @param appKey - Merchant application key
   * @param paymentIntentId - Payment Intent being initialized
   * @returns Promise resolving to TransactionSettings
   */
  public async fetchSettings(
    hubUrl: string,
    appKey: string,
    paymentIntentId: string,
  ): Promise<TransactionSettings> {
    const url = `${hubUrl}/fragments/settings/${encodeURIComponent(appKey)}?paymentIntentId=${encodeURIComponent(paymentIntentId)}`;

    const envelope = await this.apiCall<ApiResponse<TransactionSettings>>(url);

    if (!envelope.isSuccess || !envelope.value) {
      throw new Error(envelope.error?.message ?? 'Failed to load merchant settings');
    }

    return envelope.value;
  }
}
